/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useId, useState } from 'react';
import { css as unboundCss } from '@compiled/react';

import Avatar from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

const styles = cssMap({
	container: {
		display: 'flex',
		position: 'relative',
	},
	stroke: {
		position: 'absolute',
		// AvatarContent reserves 2px on each side for its outer separator.
		inset: token('space.025'),
		pointerEvents: 'none',
	},
	mask: {
		maskType: 'alpha',
	},
	motion: {
		transform: 'scale(1)',
		transition: token('motion.avatar.hovered'),
	},
	hovered: {
		transform: 'scale(1.12)',
	},
});

// Match AvatarContent's disabled surface overlay on the decorative sibling.
const disabledStyles = unboundCss({
	opacity: `calc(1 - ${token('opacity.disabled')})`,
});

export const HumanAvatarWithCenterStroke = ({
	src,
	...props
}: React.ComponentProps<typeof Avatar>): React.JSX.Element => {
	const id = useId();
	const filterId = `${id}-transparency`;
	const maskId = `${id}-stroke`;
	const [imageSrc, setImageSrc] = useState(src);
	const [hasImageErrored, setHasImageErrored] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	// Reset before committing a new source without remounting the focused avatar control.
	if (imageSrc !== src) {
		setImageSrc(src);
		setHasImageErrored(false);
	}
	const isInteractive =
		!props.isDisabled && Boolean(props.onClick || props.href || props['aria-haspopup']);

	const updateHover = (event: React.SyntheticEvent<HTMLSpanElement>) => {
		// Track AvatarContent itself, including its circular hit area, rather than the outer margin.
		setIsHovered(
			isInteractive && Boolean(event.currentTarget.firstElementChild?.matches(':hover')),
		);
	};

	return (
		<Avatar {...props} src={src}>
			<span
				css={styles.container}
				onMouseOver={updateHover}
				onMouseOut={updateHover}
				onFocus={updateHover}
				onBlur={updateHover}
				onErrorCapture={(event) => {
					if (event.target instanceof HTMLImageElement) {
						setHasImageErrored(true);
					}
				}}
			>
				<AvatarContent />
				{src && !hasImageErrored && (
					<span
						key={src}
						css={[
							styles.stroke,
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							isInteractive && fg('platform-dst-motion-uplift') && styles.motion,
							// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
							isInteractive && isHovered && fg('platform-dst-motion-uplift') && styles.hovered,
							props.isDisabled && disabledStyles,
						]}
						aria-hidden="true"
						data-testid="human-presence-center-stroke"
					>
						<svg
							width="100%"
							height="100%"
							viewBox="0 0 32 32"
							display="block"
							overflow="visible"
							focusable="false"
						>
							<defs>
								<filter
									id={filterId}
									filterUnits="userSpaceOnUse"
									primitiveUnits="userSpaceOnUse"
									x="-32"
									y="-32"
									width="96"
									height="96"
								>
									{/* Tile the unclipped image alpha so the filter's transparent edges cannot trigger the ring. */}
									<feColorMatrix
										in="SourceAlpha"
										type="matrix"
										values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0"
										x="0"
										y="0"
										width="32"
										height="32"
										result="alpha"
									/>
									<feTile in="alpha" x="-32" y="-32" width="96" height="96" result="tiled" />
									{/* Reduce each repeated tile to its minimum alpha, then reveal a complete ring if any pixel is transparent. */}
									<feMorphology
										in="tiled"
										operator="erode"
										radius="16"
										x="-1"
										y="-1"
										width="34"
										height="34"
									/>
									<feComponentTransfer result="transparent">
										<feFuncA type="linear" slope="-255" intercept="255" />
									</feComponentTransfer>
									{/* A pending or failed image has no source alpha; suppress its ring without waiting for hydration. */}
									<feMorphology
										in="tiled"
										operator="dilate"
										radius="16"
										x="-1"
										y="-1"
										width="34"
										height="34"
									/>
									<feComponentTransfer>
										<feFuncA type="linear" slope="255" />
									</feComponentTransfer>
									<feComposite in2="transparent" operator="in" />
								</filter>
								<mask
									id={maskId}
									maskUnits="userSpaceOnUse"
									css={styles.mask}
									x="-1"
									y="-1"
									width="34"
									height="34"
								>
									{/* oxlint-disable-next-line eslint-core/no-restricted-syntax -- Reference the runtime avatar URL for its alpha mask; no binary image is embedded in the source. */}
									<image
										href={src}
										width="32"
										height="32"
										preserveAspectRatio="none"
										filter={`url(#${filterId})`}
										onError={() => setHasImageErrored(true)}
									/>
								</mask>
							</defs>
							<circle
								cx="16"
								cy="16"
								r="16"
								fill="none"
								stroke={token('color.background.accent.gray.subtler')}
								strokeWidth={token('border.width')}
								vectorEffect="non-scaling-stroke"
								mask={`url(#${maskId})`}
							/>
						</svg>
					</span>
				)}
			</span>
		</Avatar>
	);
};
